import { dispatch } from 'store'
import { T } from 'consts/translationKeys'
import { translate } from 'utils/translations'
import { downloadMedia } from 'utils'
import { aiImageThunks } from 'store/features/aiImage/thunks'
import RegenerateIcon from 'assets/svgr-icons/Regenerate.svg'
import StarAnimateIcon from 'assets/svgr-icons/Star_Animate.svg'
import PaletteIcon from 'assets/svgr-icons/Palette.svg'
import DownloadIcon from 'assets/svgr-icons/Download.svg'
import RemoveIcon from 'assets/svgr-icons/Remove.svg'
import { IAIImageProject, IReferenceImage } from 'store/types/aiImage'
import { convertAiImgToBaseMediaItem } from './utils'
import { useCallback, useContext } from 'react'
import { useAddAiItemToTimeline } from 'hooks/useAddAiItemToTimeline'
import { ActiveTabContext } from 'containers/SubSidebar/AiImageContent/contextApi'
import { IActionData } from 'common-components/ActionsOverlay/types'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import { useAlertPopupsContext } from 'context/AlertPopups'

export const useGetAiImageActions = (item: IAIImageProject) => {
  const { filePath, id: assetId } = item.images[0]
  const { setAlertPopupsData } = useAlertPopupsContext()

  const { setActiveTabKey } = useContext(ActiveTabContext)
  const handleAnimate = () => {
    dispatch(
      aiVideoThunks.animateAiImage({
        assetId,
        isAiImage: true
      })
    )
  }

  const switchToGenerateTab = useCallback(() => {
    setActiveTabKey('generate_tab_key')
  }, [setActiveTabKey])

  const handleDownload = useCallback(async () => {
    if (!filePath) return

    await downloadMedia({
      filePath,
      fileName: `ai-image-${assetId}`
    })
  }, [filePath, assetId])

  const addItemToTimeline = useAddAiItemToTimeline()

  const handleAddToTimeline = useCallback(() => {
    const mediaItem = convertAiImgToBaseMediaItem(item)
    addItemToTimeline(mediaItem)
  }, [item, addItemToTimeline])

  const handleCreateSimilar = useCallback(() => {
    switchToGenerateTab()
    dispatch(aiImageThunks.setGenerationParamsByAssetId(Number(assetId)))
  }, [switchToGenerateTab, assetId])

  const handleUseAsReference = useCallback(() => {
    switchToGenerateTab()
    const { id, images, width, height } = item

    const referenceImage: IReferenceImage = {
      id,
      url: filePath,
      thumbnailUrl: images[0].thumbnailPath || filePath,
      width,
      height
    }
    dispatch(aiImageThunks.replaceReferenceImages([referenceImage]))
    dispatch(aiImageThunks.setPrompt(''))
    dispatch(aiImageThunks.setTargetMediaItemId(null))
  }, [item, filePath, switchToGenerateTab])

  const handleDelete = useCallback(() => {
    dispatch(aiImageThunks.deleteProjectById(item.id))
  }, [item.id])

  const handleDeleteWarning = useCallback(() => {
    setAlertPopupsData({
      warningPopup: {
        title: translate(T.DELETE_AI_IMAGE_CONFIRMATION_TITLE),
        description: translate(T.DELETE_AI_IMAGE_CONFIRMATION_DESCRIPTION),
        buttons: {
          cancel: {
            label: translate(T.CANCEL_LABEL)
          },
          confirm: {
            label: translate(T.DELETE_LABEL),
            onClick: handleDelete
          }
        }
      }
    })
  }, [setAlertPopupsData, handleDelete])

  //primary actions are the actions that are displayed in the item when hovering, secondary actions are the actions that are displayed in context menu
  const primaryActions: IActionData[] = [
    {
      key: T.CREATE_SIMILAR,
      title: translate(T.CREATE_SIMILAR),
      icon: <RegenerateIcon />,
      onClick: handleCreateSimilar
    },
    {
      key: T.USE_AS_REFERENCE,
      title: translate(T.USE_AS_REFERENCE),
      icon: <PaletteIcon />,
      onClick: handleUseAsReference
    },
    {
      key: T.ANIMATE,
      title: translate(T.CUSTOM_ANIMATE),
      icon: <StarAnimateIcon />,
      onClick: handleAnimate
    },
    {
      key: T.DOWNLOAD,
      title: translate(T.DOWNLOAD),
      icon: <DownloadIcon />,
      onClick: handleDownload
    },
    {
      key: T.DELETE_LABEL,
      title: translate(T.DELETE_LABEL),
      icon: <RemoveIcon />,
      onClick: handleDeleteWarning
    }
  ]

  return {
    mainAction: handleAddToTimeline,
    primaryActions
  }
}
